import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LocationPicker } from "@/components/LocationPicker";

/**
 * Página PÚBLICA (sin auth) de captura de ubicación del prospecto.
 * /captura/:token. fetch() plano (no el cliente `api`, que redirige a /login).
 *
 * Diseño (rc75): tema "noche / señal" tipo radar GPS (basado en el mockup que
 * aprobó Alex). Pasos: 1 datos → 2 captura (radar + anillo 30s) → 3 resumen.
 * Reglas: permiso denegado BLOQUEA (con reintentar), bloqueo en PC, sin "modo
 * demo" (nada de coords falsas en prod), envío real, botón habilitado con buena
 * precisión. Acento = branding.color_primary (cae al teal del mockup).
 */

const BASE = "/api/v1/prospectos/publico";
const DURACION = 30; // segundos de captura
const META_PRECISION = 15; // m: buena precisión
const STORAGE_PREFIX = "byteisp_captura_";
const CIRC = 2 * Math.PI * 110; // circunferencia del aro

function detectMobile(): boolean {
  if (typeof navigator === "undefined") return true;
  const ua = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const coarse =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(pointer: coarse)").matches
      : false;
  return ua || coarse;
}

// rc126 — el enlace suele llegar por WhatsApp: en ANDROID los navegadores
// internos (WebView de WhatsApp/Facebook/Instagram/Messenger) tienen la
// geolocalización capada — no aparece el prompt de permiso y watchPosition
// muere en silencio. En iPhone sí funcionan → "en Android no jala".
function esAndroid(): boolean {
  return typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);
}
function detectInApp(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return esAndroid() && /(; wv\)|FB_IAB|FBAN|FBAV|Instagram|Line\/|WhatsApp)/i.test(ua);
}

type Branding = { nombre: string; logo_url: string | null; color_primary: string | null };
type Info = {
  folio: string;
  nombre_completo: string | null;
  telefono: string | null;
  ya_capturado: boolean;
  branding: Branding;
};
type Page = "loading" | "ok" | "invalid" | "expired";
type Captura = "listo" | "buscando" | "denied" | "capturado";

export function CapturaPublicaPage() {
  const { token = "" } = useParams<{ token: string }>();

  const [page, setPage] = useState<Page>("loading");
  const [info, setInfo] = useState<Info | null>(null);
  const [isMobile] = useState(detectMobile);
  const [inApp] = useState(detectInApp);
  const [copiado, setCopiado] = useState(false);
  const [gpsApagado, setGpsApagado] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");

  const [cap, setCap] = useState<Captura>("listo");
  const [restante, setRestante] = useState(DURACION);
  const [lecturas, setLecturas] = useState(0);
  const [mejor, setMejor] = useState<{ lat: number; lng: number; acc: number } | null>(null);
  const [enviando, setEnviando] = useState(false);

  const watchRef = useRef<number | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const senal = info?.branding.color_primary || "#34e6c0";

  // ── Cargar info por token ───────────────────────────────────────────────
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/${encodeURIComponent(token)}`);
        if (cancel) return;
        if (r.status === 404) return setPage("invalid");
        if (r.status === 410) return setPage("expired");
        if (!r.ok) return setPage("invalid");
        const data: Info = await r.json();
        if (cancel) return;
        setInfo(data);
        let saved: { nombre?: string; telefono?: string; step?: number } | null = null;
        try {
          const raw = sessionStorage.getItem(STORAGE_PREFIX + token);
          if (raw) saved = JSON.parse(raw);
        } catch {
          /* ignore */
        }
        setNombre(saved?.nombre ?? data.nombre_completo ?? "");
        setTelefono(saved?.telefono ?? data.telefono ?? "");
        if (data.ya_capturado) setStep(3);
        else if (saved?.step === 2) setStep(2);
        setPage("ok");
      } catch {
        if (!cancel) setPage("invalid");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [token]);

  const detener = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (watchRef.current !== null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  useEffect(() => () => detener(), [detener]);

  useEffect(() => {
    if (page !== "ok") return;
    try {
      sessionStorage.setItem(STORAGE_PREFIX + token, JSON.stringify({ nombre, telefono, step }));
    } catch {
      /* ignore */
    }
  }, [token, nombre, telefono, step, page]);

  // ── Captura GPS ─────────────────────────────────────────────────────────
  function iniciarCaptura() {
    if (!navigator.geolocation) {
      setCap("denied");
      return;
    }
    detener();
    setCap("buscando");
    setGpsApagado(false);
    setRestante(DURACION);
    setLecturas(0);
    setMejor(null);
    let r = DURACION;
    let best: { lat: number; lng: number; acc: number } | null = null;
    tickRef.current = setInterval(() => {
      r -= 1;
      setRestante(Math.max(r, 0));
      if (r <= 0) {
        detener();
        setCap("capturado");
      }
    }, 1000);
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const acc = Math.round(pos.coords.accuracy);
        setLecturas((n) => n + 1);
        if (!best || acc < best.acc) {
          best = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc };
          setMejor(best);
        }
        if (best.acc <= META_PRECISION) {
          detener();
          setCap("capturado");
        }
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          detener();
          setCap("denied");
        } else {
          // rc126 — POSITION_UNAVAILABLE / TIMEOUT: en Android casi siempre es
          // la ubicación del TELÉFONO apagada (ajustes rápidos) o el WebView
          // de una app. Antes se ignoraba y el radar giraba 30 s para nada.
          detener();
          setGpsApagado(true);
          setCap("capturado"); // cae a la pantalla de "sin lecturas" con el aviso
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 27000 },
    );
  }

  // rc126 — copiado que también funciona en WebViews viejos (sin clipboard API)
  async function copiarEnlace() {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
      return;
    } catch {
      /* fallback abajo */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* último recurso: que lo copien del banner con long-press */
    }
  }

  async function retryPermission() {
    try {
      const navAny = navigator as Navigator & {
        permissions?: { query: (d: { name: PermissionName }) => Promise<{ state: string }> };
      };
      if (navAny.permissions?.query) {
        const st = await navAny.permissions.query({ name: "geolocation" as PermissionName });
        if (st.state === "granted" || st.state === "prompt") {
          setCap("listo");
          iniciarCaptura();
          return;
        }
      }
    } catch {
      /* ignore */
    }
    window.location.reload();
  }

  const buena = mejor !== null && mejor.acc <= META_PRECISION;
  // Progreso del aro por CALIDAD de señal: se llena conforme baja el margen de
  // error (100 m → 0%, ≤15 m → 100%). No por tiempo.
  const qual = mejor ? Math.max(0, Math.min(1, (100 - mejor.acc) / (100 - META_PRECISION))) : 0;

  async function enviar() {
    if (!mejor) return;
    setEnviando(true);
    try {
      const r = await fetch(`${BASE}/${encodeURIComponent(token)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre_completo: nombre.trim(),
          telefono: telefono.trim(),
          lat: mejor.lat,
          lng: mejor.lng,
          precision_m: mejor.acc,
        }),
      });
      if (r.ok || r.status === 409) {
        detener();
        try {
          sessionStorage.removeItem(STORAGE_PREFIX + token);
        } catch {
          /* ignore */
        }
        setStep(3);
      } else {
        const body = await r.json().catch(() => ({}));
        alert(body?.detail ?? "No se pudo enviar. Verifica tu conexión e inténtalo de nuevo.");
      }
    } catch {
      alert("No se pudo enviar. Verifica tu conexión e inténtalo de nuevo.");
    } finally {
      setEnviando(false);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  const styleTag = <style>{CSS_NOCHE}</style>;
  const cssVars = { ["--senal" as string]: senal } as React.CSSProperties;

  if (page === "loading") {
    return (
      <div className="cap-bg" style={cssVars}>
        {styleTag}
        <p style={{ color: "#8fa3bd" }}>Cargando…</p>
      </div>
    );
  }

  if (page === "invalid" || page === "expired") {
    return (
      <div className="cap-bg" style={cssVars}>
        {styleTag}
        <main className="cap-app">
          <section className="tarjeta" style={{ textAlign: "center" }}>
            <div className="eyebrow">{page === "expired" ? "Enlace expirado" : "Enlace no válido"}</div>
            <h1>{page === "expired" ? "El enlace expiró" : "Link no válido"}</h1>
            <p className="sub">
              {page === "expired"
                ? "Por seguridad este link tiene tiempo límite. Pídele a tu proveedor uno nuevo."
                : "Este enlace no existe o ya no está disponible. Pídele a tu proveedor uno nuevo."}
            </p>
          </section>
        </main>
      </div>
    );
  }

  if (!isMobile) {
    return (
      <div className="cap-bg" style={cssVars}>
        {styleTag}
        <main className="cap-app">
          <Marca brand={info?.branding} />
          <section className="tarjeta" style={{ textAlign: "center" }}>
            <div className="eyebrow">Solo desde celular</div>
            <h1>Ábrelo en tu teléfono 📱</h1>
            <p className="sub">
              Para registrar tu ubicación necesitamos el GPS de tu celular. Abre este mismo enlace
              desde tu teléfono para continuar — desde una computadora no es posible.
            </p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="cap-bg" style={cssVars}>
      {styleTag}
      <div className="ambiente" aria-hidden="true"><span /><span /><span /></div>
      <main className="cap-app">
        <Marca brand={info?.branding} />
        {inApp && step !== 3 && (
          <section className="tarjeta" style={{ marginBottom: 14 }}>
            <div className="eyebrow">Un paso antes</div>
            <h1 style={{ fontSize: 20 }}>Ábrelo en tu navegador</h1>
            <p className="sub">
              El navegador de esta app no permite leer tu GPS. Toca el botón para abrirlo en tu
              navegador (Chrome, Samsung Internet, Firefox…) — o usa el menú ⋮ de arriba y elige
              <b> “Abrir en el navegador”</b>.
            </p>
            <a
              className="btn"
              // intent genérico SIN package: Android lo manda al navegador
              // PREDETERMINADO del teléfono, sea cual sea.
              href={`intent://${window.location.host}/captura/${encodeURIComponent(token)}#Intent;scheme=https;action=android.intent.action.VIEW;end`}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M3.5 12h17M12 3.5c2.6 2.3 4 5.2 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.2-4-8.5s1.4-6.2 4-8.5z" /></svg>
              Abrir en mi navegador
            </a>
            <button className="btn fantasma" onClick={copiarEnlace}>
              {copiado ? "¡Copiado! Pégalo en tu navegador" : "Copiar el enlace"}
            </button>
          </section>
        )}
        <section className="tarjeta">

          {step === 1 && (
            <div className="paso activo">
              <div className="eyebrow">Confirmación de instalación</div>
              <h1>Marquemos tu ubicación exacta</h1>
              <p className="sub">
                Necesitamos saber a dónde llevar tu servicio. Anota tus datos y enseguida ubicamos el
                domicilio desde tu celular.
              </p>
              <label>¿Quién solicita el servicio?</label>
              <input className="campo" value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Nombre completo" autoComplete="name" />
              <label style={{ marginTop: 16 }}>Teléfono (WhatsApp)</label>
              <input className="campo" value={telefono} onChange={(e) => setTelefono(e.target.value)} placeholder="Ej. 271 123 4567" inputMode="tel" />
              <button className="btn mt" disabled={nombre.trim().length < 3 || telefono.trim().length < 8} onClick={() => setStep(2)}>
                Continuar
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="paso activo">
              <div className="eyebrow">Ubicando domicilio</div>
              <h1>{cap === "denied" ? "Necesitamos tu ubicación" : `${primerNombre(nombre)}, mantén el celular en el domicilio`}</h1>

              {cap === "denied" ? (
                <>
                  <p className="sub">
                    Para continuar debes <b>permitir el acceso a tu ubicación</b>. Actívalo en el aviso de
                    tu navegador (o en los ajustes del sitio) y vuelve a intentar.
                  </p>
                  <button className="btn" onClick={retryPermission}>Permitir y reintentar</button>
                  <button className="btn fantasma" onClick={() => setStep(1)}>← Volver</button>
                </>
              ) : (
                <>
                  <p className="sub">
                    Vamos a leer tu GPS hasta {DURACION} s para afinar la precisión. Quédate al aire libre o
                    cerca de una ventana mientras tanto.
                  </p>

                  <div className="radar-wrap">
                    <div className={"radar" + (cap === "buscando" ? " escaneando" : "")}>
                      <span className="pulso" /><span className="pulso" /><span className="pulso" />
                      <svg className="aro" viewBox="0 0 230 230">
                        <circle className="pista" cx="115" cy="115" r="110" />
                        <circle className="avance" cx="115" cy="115" r="110"
                          style={{ strokeDasharray: CIRC, strokeDashoffset: CIRC * (1 - qual), stroke: buena ? "#34e6c0" : undefined }} />
                      </svg>
                      <div className="nucleo">
                        <svg className="pin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.6" /></svg>
                        <div className="cuenta">
                          {cap === "capturado"
                            ? (buena ? "✓" : mejor ? <>±{mejor.acc}<small>m</small></> : "—")
                            : cap === "buscando"
                            ? <>{restante}<small>s</small></>
                            : <>{DURACION}<small>s</small></>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="metricas">
                    <div className="metrica">
                      <div className={"v" + (mejor ? (mejor.acc <= META_PRECISION ? "" : mejor.acc <= 40 ? " regular" : " malo") : "")}>
                        {mejor ? `± ${mejor.acc} m` : "—"}
                      </div>
                      <div className="k">Precisión</div>
                    </div>
                    <div className="metrica"><div className="v">{lecturas}</div><div className="k">Lecturas</div></div>
                  </div>

                  <div className="estado">
                    <span className={"punto" + (buena ? " ok" : "")} />
                    <span>
                      {cap === "listo" && "Listo para ubicar"}
                      {cap === "buscando" && (mejor ? (buena ? "Buena señal, afinando…" : "Mejorando precisión…") : "Buscando señal GPS…")}
                      {cap === "capturado" && (buena ? "Ubicación lista" : "Precisión limitada")}
                    </span>
                  </div>

                  {mejor && (
                    <div className="mapa-vivo">
                      <LocationPicker value={{ lat: mejor.lat, lng: mejor.lng }} onChange={() => {}} readOnly tiles="satellite" heightPx={170} />
                    </div>
                  )}

                  {cap === "listo" && (
                    <button className="btn" onClick={iniciarCaptura}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3.2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></svg>
                      Tomar mi ubicación
                    </button>
                  )}
                  {cap === "capturado" && buena && (
                    <button className="btn" disabled={enviando} onClick={enviar}>
                      {enviando ? "Enviando…" : "Sí, es aquí — enviar ✓"}
                    </button>
                  )}
                  {cap === "capturado" && mejor && !buena && (
                    <>
                      <div className="nota">
                        La precisión quedó en ± {mejor.acc} m. Para mejorarla, sal al aire libre o
                        acércate a una ventana y vuelve a intentar.
                      </div>
                      <button className="btn" onClick={iniciarCaptura}>Reintentar</button>
                      <button className="btn fantasma" disabled={enviando} onClick={enviar}>
                        {enviando ? "Enviando…" : `Enviar de todos modos (± ${mejor.acc} m)`}
                      </button>
                    </>
                  )}
                  {cap === "capturado" && !mejor && (
                    <>
                      <div className="nota">
                        {gpsApagado
                          ? "Tu teléfono no está entregando la ubicación. Revisa que la Ubicación (GPS) esté ENCENDIDA en los ajustes rápidos del teléfono y vuelve a intentar."
                          : "No pudimos fijar tu GPS. Sal al aire libre o acércate a una ventana e inténtalo de nuevo."}
                      </div>
                      <button className="btn" onClick={iniciarCaptura}>Reintentar</button>
                    </>
                  )}
                  {cap !== "buscando" && <button className="btn fantasma" onClick={() => setStep(1)}>← Volver</button>}
                </>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="paso activo">
              <div className="check" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
              </div>
              <h1 style={{ textAlign: "center" }}>¡Ubicación registrada!</h1>
              <p className="sub" style={{ textAlign: "center" }}>
                Ya tenemos tu domicilio. Nuestro equipo de {info?.branding.nombre ?? "Bytecom"} se pondrá en
                contacto para agendar la visita de instalación.
              </p>
              <div className="dato"><span className="k">Solicitante</span><span className="v">{nombre || "—"}</span></div>
              {mejor && <div className="dato"><span className="k">Coordenadas</span><span className="v mono">{mejor.lat.toFixed(6)}, {mejor.lng.toFixed(6)}</span></div>}
              {mejor && <div className="dato"><span className="k">Precisión</span><span className="v">± {mejor.acc} m</span></div>}
              <div className="dato"><span className="k">Estado</span><span className="pill">Enviado ✓</span></div>
              {mejor && (
                <a className="btn mt" href={`https://www.google.com/maps?q=${mejor.lat},${mejor.lng}`} target="_blank" rel="noopener noreferrer">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 7v13l6-3 6 3 6-3V4l-6 3-6-3z" /><path d="M9 4v13M15 7v13" /></svg>
                  Ver en el mapa
                </a>
              )}
            </div>
          )}

        </section>
        <p className="pie">Tus datos solo se usan para coordinar tu instalación · {info?.branding.nombre ?? "Bytecom"}</p>
      </main>
    </div>
  );
}

function primerNombre(n: string): string {
  return (n.trim().split(/\s+/)[0] || "Hola");
}

function Marca({ brand }: { brand?: Branding | null }) {
  return (
    <div className="marca">
      {brand?.logo_url ? (
        <img src={brand.logo_url} alt={brand.nombre} style={{ height: 38, objectFit: "contain" }} />
      ) : (
        <>
          <div className="logo" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="#04140f" strokeWidth="2.2" strokeLinecap="round"><path d="M5 13a10 10 0 0 1 14 0" /><path d="M8.5 16.5a5 5 0 0 1 7 0" /><circle cx="12" cy="20" r="1.3" fill="#04140f" stroke="none" /></svg>
          </div>
          <b>{(brand?.nombre ?? "Bytecom").toUpperCase()}</b>
        </>
      )}
    </div>
  );
}

const CSS_NOCHE = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap');
.cap-bg{--noche:#091122;--panel:rgba(255,255,255,.04);--linea:rgba(255,255,255,.10);--ambar:#ffb454;--texto:#eef3f8;--texto-mute:#8fa3bd;--display:'Space Grotesk',system-ui,sans-serif;--body:'Inter',system-ui,sans-serif;
  font-family:var(--body);color:var(--texto);min-height:100dvh;display:flex;align-items:center;justify-content:center;padding:24px 18px;overflow-x:hidden;-webkit-font-smoothing:antialiased;
  background:radial-gradient(1200px 700px at 50% -10%,#12244099 0%,transparent 60%),radial-gradient(900px 600px at 100% 110%,#0b203a88 0%,transparent 55%),var(--noche)}
.cap-bg *{box-sizing:border-box}
.ambiente{position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:0}
.ambiente span{position:absolute;left:50%;top:14%;width:10px;height:10px;border-radius:50%;border:1px solid var(--senal);transform:translate(-50%,-50%);opacity:0;animation:cap-amb 9s linear infinite}
.ambiente span:nth-child(2){animation-delay:3s}.ambiente span:nth-child(3){animation-delay:6s}
@keyframes cap-amb{0%{opacity:.18;width:10px;height:10px}100%{opacity:0;width:1100px;height:1100px}}
.cap-app{position:relative;z-index:1;width:100%;max-width:430px}
.marca{display:flex;align-items:center;gap:11px;justify-content:center;margin-bottom:26px;animation:cap-cae .7s cubic-bezier(.2,.7,.2,1) both}
.logo{width:40px;height:40px;border-radius:12px;display:grid;place-items:center;background:var(--senal);box-shadow:0 8px 26px color-mix(in srgb,var(--senal) 35%,transparent)}
.logo svg{width:22px;height:22px}
.marca b{font-family:var(--display);font-weight:700;font-size:1.22rem;letter-spacing:.2px}
.tarjeta{background:var(--panel);border:1px solid var(--linea);border-radius:24px;padding:30px 24px 26px;backdrop-filter:blur(14px);box-shadow:0 30px 80px -30px rgba(0,0,0,.7);position:relative;overflow:hidden}
.paso{animation:cap-entra .5s cubic-bezier(.2,.7,.2,1) both}
@keyframes cap-entra{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
@keyframes cap-cae{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:none}}
.eyebrow{font-family:var(--display);font-size:.72rem;font-weight:600;letter-spacing:2.5px;text-transform:uppercase;color:var(--senal);margin-bottom:10px}
.tarjeta h1{font-family:var(--display);font-weight:600;font-size:1.5rem;line-height:1.2;margin-bottom:8px}
.sub{color:var(--texto-mute);font-size:.95rem;line-height:1.5;margin-bottom:22px}
.tarjeta label{display:block;font-size:.82rem;font-weight:600;color:var(--texto-mute);margin-bottom:8px;letter-spacing:.3px}
.campo{width:100%;background:rgba(255,255,255,.05);border:1.5px solid var(--linea);color:var(--texto);font-family:var(--body);font-size:1.05rem;padding:15px 16px;border-radius:14px;transition:border-color .2s,box-shadow .2s,background .2s}
.campo::placeholder{color:#5f7390}
.campo:focus{outline:none;border-color:var(--senal);background:color-mix(in srgb,var(--senal) 6%,transparent);box-shadow:0 0 0 4px color-mix(in srgb,var(--senal) 14%,transparent)}
.btn{width:100%;border:none;cursor:pointer;font-family:var(--display);font-weight:600;font-size:1.05rem;letter-spacing:.3px;padding:16px;border-radius:14px;color:#04140f;background:var(--senal);display:flex;align-items:center;justify-content:center;gap:9px;transition:transform .12s ease,box-shadow .2s,opacity .2s;box-shadow:0 12px 30px -8px color-mix(in srgb,var(--senal) 50%,transparent);margin-top:8px}
.btn:active{transform:scale(.99)}
.btn:disabled{opacity:.45;cursor:not-allowed;box-shadow:none}
.btn.fantasma{background:transparent;border:1.5px solid var(--linea);color:var(--texto);box-shadow:none;font-weight:500;margin-top:10px}
.btn svg{width:19px;height:19px}
.mt{margin-top:20px}
.radar-wrap{display:flex;justify-content:center;margin:6px 0 20px}
.radar{position:relative;width:220px;height:220px;display:grid;place-items:center}
.pulso{position:absolute;width:60px;height:60px;border-radius:50%;border:1.5px solid var(--senal);opacity:0}
.escaneando .pulso{animation:cap-pulso 2.4s ease-out infinite}
.escaneando .pulso:nth-child(2){animation-delay:.8s}.escaneando .pulso:nth-child(3){animation-delay:1.6s}
@keyframes cap-pulso{0%{opacity:.55;width:60px;height:60px}100%{opacity:0;width:220px;height:220px}}
.aro{position:absolute;width:220px;height:220px;transform:rotate(-90deg)}
.aro circle{fill:none;stroke-width:5;stroke-linecap:round}
.aro .pista{stroke:rgba(255,255,255,.08)}
.aro .avance{stroke:var(--senal);transition:stroke-dashoffset .35s linear}
.nucleo{position:relative;width:128px;height:128px;border-radius:50%;display:grid;place-items:center}
.pin{width:44px;height:44px;color:var(--senal)}
.escaneando .pin{animation:cap-flota 2.2s ease-in-out infinite}
@keyframes cap-flota{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
.cuenta{position:absolute;bottom:16px;font-family:var(--display);font-weight:700;font-size:1.05rem;color:var(--texto)}
.cuenta small{font-weight:400;font-size:.7rem;color:var(--texto-mute)}
.metricas{display:flex;gap:12px;margin-bottom:8px}
.metrica{flex:1;background:rgba(255,255,255,.04);border:1px solid var(--linea);border-radius:14px;padding:13px;text-align:center}
.metrica .v{font-family:var(--display);font-weight:700;font-size:1.3rem;color:var(--senal);transition:color .3s}
.metrica .v.malo,.metrica .v.regular{color:var(--ambar)}
.metrica .k{font-size:.66rem;color:var(--texto-mute);letter-spacing:.6px;text-transform:uppercase;margin-top:4px}
.estado{display:flex;align-items:center;gap:9px;justify-content:center;font-size:.9rem;color:var(--texto-mute);min-height:24px;margin:16px 0 4px}
.punto{width:9px;height:9px;border-radius:50%;background:var(--ambar);animation:cap-late 1.4s infinite}
.punto.ok{background:var(--senal);animation:none}
.nota{background:rgba(255,180,84,.08);border:1px solid rgba(255,180,84,.25);border-radius:13px;padding:13px 14px;font-size:.85rem;color:#ffd9a8;line-height:1.45;margin-top:8px}
.mapa-vivo{border-radius:16px;overflow:hidden;border:1px solid color-mix(in srgb,var(--senal) 35%,var(--linea));margin:6px 0 14px;box-shadow:0 10px 30px -16px rgba(0,0,0,.7)}
@keyframes cap-late{0%{box-shadow:0 0 0 0 rgba(255,180,84,.5)}70%{box-shadow:0 0 0 9px rgba(255,180,84,0)}100%{box-shadow:0 0 0 0 rgba(255,180,84,0)}}
.check{width:72px;height:72px;margin:4px auto 18px;border-radius:50%;display:grid;place-items:center;background:color-mix(in srgb,var(--senal) 14%,transparent);border:1.5px solid var(--senal)}
.check svg{width:34px;height:34px;color:var(--senal)}
.dato{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:14px 0;border-bottom:1px solid var(--linea)}
.dato:last-of-type{border-bottom:none}
.dato .k{font-size:.82rem;color:var(--texto-mute)}
.dato .v{font-family:var(--display);font-weight:600;font-size:.95rem;text-align:right;word-break:break-word}
.dato .v.mono{font-variant-numeric:tabular-nums}
.pill{font-size:.72rem;font-weight:600;padding:3px 9px;border-radius:20px;background:color-mix(in srgb,var(--senal) 14%,transparent);color:var(--senal);font-family:var(--body)}
.pie{text-align:center;margin-top:20px;font-size:.74rem;color:#5f7390;letter-spacing:.3px}
@media (prefers-reduced-motion:reduce){.cap-bg *{animation-duration:.001ms!important;animation-iteration-count:1!important}.ambiente{display:none}}
`;
